import React, { useEffect, useState } from "react";
import BlockUi from "react-block-ui";
import InfiniteScroll from "react-infinite-scroll-component";

import { Close } from "@mui/icons-material";
import { Box, Container, Stack, Typography } from "@mui/material";

import EmptyError from "../EmptyError/EmptyError";
import AgentService from "../../Services/AgentService";
import PartnersSearch from "../Partners/PartnersSearch";
import FieldAgentReturnCard from "./FieldAgentReturnCard";
import AnalyticsService from "../../Services/AnalyticsService";

const IN_PROGRESS_STATUS_FILTERS = [
  { name: "Bilty Pending", key: "BILTY_UPLOAD_PENDING", selected: false },
  { name: "Return in Transit", key: "RETURN_IN_TRANSIT", selected: false },
  { name: "Received", key: "RECEIVED", selected: false },
];

export default function InProgressReturnOrders() {
  const INPROGRESS = "INPROGRESS";
  const [searchInfo, setSearchInfo] = useState("");
  const [loading, setLoading] = useState("not_started");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [isFilterChanged, setIsFilterChanged] = useState(false);
  const [returnOrdersList, setReturnOrdersList] = useState([]);
  const [currentPageCount, setCurrentPageCount] = useState(1);

  useEffect(() => {
    const returnOrdersCB = getReturnOrders();
    return () => returnOrdersCB();
  }, [searchKeyword, isFilterChanged]);

  const getReturnOrders = () => {
    setLoading("started");
    const { axiosRequest, abortController } =
      AgentService.getFieldAgentTicketOrders(
        currentPageCount,
        searchKeyword,
        INPROGRESS,
        getOrderStatusesSelected()
      );
    axiosRequest
      .then((response) => {
        const orders = response?.data?.responseData?.orders;
        if (orders && orders.length) {
          setReturnOrdersList((prevList) => [...prevList, ...orders]);
          setCurrentPageCount(currentPageCount + 1);
          setHasMoreOrders(true);
        } else {
          setHasMoreOrders(false);
        }
        setLoading("done");
      })
      .catch((error) => {
        if (error?.message !== "canceled") {
          setHasMoreOrders(false);
          setLoading("done");
          console.error("Error getting In Progress Return Orders: ", error);
        }
      });
    return () => abortController.abort();
  };

  function initializePagination() {
    setReturnOrdersList([]);
    setCurrentPageCount(1);
    setHasMoreOrders(true);
  }

  const getOrderStatusesSelected = () => {
    return IN_PROGRESS_STATUS_FILTERS.reduce((acc, filter) => {
      if (filter.selected) {
        acc.push(filter.key);
      }
      return acc;
    }, []).join("&orderStatus=");
  };

  const handleSearch = (keyword) => {
    if (keyword) {
      if (keyword?.length < 5) {
        return setSearchInfo("Type minimum 5 characters!");
      }
      setSearchInfo("");
      setSearchKeyword(keyword);
      initializePagination();
    } else {
      clearSearch();
    }
  };

  const clearSearch = () => {
    setSearchKeyword("");
    initializePagination();
  };

  const handleFilterClick = (filterClicked) => {
    AnalyticsService.pushEvent(`Filter Clicked`, {
      page: window.location.pathname || "",
      filterLabel: filterClicked?.name || "",
      filterStatus: filterClicked?.selected ? "Applied" : "Removed",
    });

    const selectedStatusFilter = IN_PROGRESS_STATUS_FILTERS.find(
      ({ name }) => name === filterClicked.name
    );
    selectedStatusFilter.selected = !selectedStatusFilter.selected;

    initializePagination();
    setIsFilterChanged(!isFilterChanged);
  };

  return (
    <div>
      <Container maxWidth={`xl`} sx={{ pb: 1 }}>
        <PartnersSearch
          errorMessage={searchInfo}
          handleSearch={handleSearch}
          clearSearch={clearSearch}
          searchKeyword={searchKeyword}
          page={"Agent In Progress Return Orders"}
        />

        <Stack
          direction="row"
          spacing={1}
          sx={{
            margin: "4px 0px",
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {IN_PROGRESS_STATUS_FILTERS.map((filter, index) => (
            <Box
              key={index}
              sx={{
                boxShadow: "0px 0px 6px rgba(28, 28, 28, 0.1);",
                borderRadius: "16px",
                padding: "4px 12px",
                display: "flex",
                alignItems: "center",
                fontWeight: 700,
                color: filter.selected ? "#fff" : "#4f4f4f",
                backgroundColor: filter.selected
                  ? "#9D2123 !important"
                  : "#FFFFFF !important",
              }}
              onClick={() => handleFilterClick(filter)}
            >
              <Typography noWrap sx={{ fontSize: "13px", fontWeight: 700 }}>
                {filter.name}
              </Typography>
              {filter.selected && (
                <Close sx={{ fontSize: "18px", marginLeft: "4px" }} />
              )}
            </Box>
          ))}
        </Stack>
      </Container>

      <BlockUi
        tag="div"
        keepInView
        message={`Loading...`}
        blocking={loading === "started"}
        style={{ minHeight: "85vh" }}
      >
        {returnOrdersList?.length ? (
          <InfiniteScroll
            dataLength={returnOrdersList.length}
            next={getReturnOrders}
            hasMore={hasMoreOrders}
            loader={<p style={{ textAlign: "center" }}>Loading...</p>}
            style={{ padding: "10px 16px 125px 16px" }}
          >
            {returnOrdersList?.map((order) => (
              <FieldAgentReturnCard
                key={order.orderId}
                data={order}
                tabName={"In Progress"}
              />
            ))}
          </InfiniteScroll>
        ) : (
          loading === "done" && (
            <EmptyError
              image="noComplaints.svg"
              text="No Orders Found"
              hideBack={true}
            />
          )
        )}
      </BlockUi>
    </div>
  );
}
